import { XAT_API } from '@/services/api-paths';

export const ALLPOWERS_WIKI_URL = 'https://wiki.xat.com/Allpowers';
export const ALLPOWERS_STORE_URL = 'https://xat.com/getpowers';
export const ALLPOWERS_FAIRTRADE_URL = 'https://xatblog.net/fairtrade/';

export const ALLPOWERS_DESCRIPTION = {
  short: 'Conquista ao possuir um exemplar de cada power do conjunto AllPowers.',
  long: 'AllPowers não é um power — é uma funcionalidade especial activada quando você possui pelo menos um exemplar de cada power que faz parte do conjunto. Desbloqueia o chapéu Golden Egg, efeitos exclusivos nos smilies e o smilie (allpowers). Novos powers podem ser adicionados ao conjunto sem aviso; é necessário adquiri-los para recuperar o AllPowers.',
} as const;

export type AllpowersRequiredPower = {
  id: number;
  name: string;
  slug: string;
  category: string;
  limited: boolean;
};

export type AllpowersRequiredResponse = {
  code: number;
  count: number;
  updatedAt: string;
  source: string;
  powers: AllpowersRequiredPower[];
};

export type AllpowersCodeExample = {
  code: string;
  label: string;
  hint?: string;
};

export const ALLPOWERS_CODE_EXAMPLES: AllpowersCodeExample[] = [
  {
    code: '(smile#allpowers)',
    label: 'Rocket à volta do smiley',
  },
  {
    code: '(blueman#allpowers#w1)',
    label: 'Anel em vez de rocket',
    hint: 'Substitua #w1 por outro carácter (#w?) para variar o anel.',
  },
  {
    code: '(hat#e)',
    label: 'Chapéu Golden Egg',
  },
  {
    code: '(hat#e#FFD800)',
    label: 'Ovo colorido',
    hint: 'Desactive o AllPowers primeiro e acrescente o código de cor após #e.',
  },
  {
    code: '(hat#g#FFD800)',
    label: 'Ovo colorido (sem gifts)',
    hint: 'Se não tiver gifts, use #g em vez de #e para colorir sem desactivar AllPowers.',
  },
];

const POWER_ROW_RE = /<tr class="power-[^"]*">[\s\S]*?<\/tr>/gi;
const TD_RE = /<td[^>]*>([\s\S]*?)<\/td>/gi;

function stripHtml(value: string): string {
  return value.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

function cleanCategory(value: string): string {
  return stripHtml(value).replace(/allpowers$/i, '').trim();
}

/** Extrai powers com coluna AllPowers marcada na tabela FairTrade do xatblog. */
export function parseAllpowersRequiredFromFairtradeHtml(html: string): AllpowersRequiredPower[] {
  const rows = html.match(POWER_ROW_RE) ?? [];

  return rows
    .map((row) => {
      const cells = [...row.matchAll(TD_RE)].map((match) => match[1]);
      if (cells.length < 10) return null;

      const slug = row.match(/data-power="([^"]+)"/i)?.[1]?.trim().toLowerCase() ?? '';
      const name = stripHtml(cells[1] ?? '');
      const id = Number.parseInt(stripHtml(cells[0] ?? ''), 10);
      const statusText = stripHtml(cells[3] ?? '');
      const limited = /limited/i.test(statusText) && !/unlimited/i.test(statusText);
      const isAllpowers = (cells[9] ?? '').includes('fa-check text-success');

      if (!slug || !name || !Number.isFinite(id) || !isAllpowers) return null;

      return {
        id,
        name,
        slug,
        category: cleanCategory(cells[2] ?? ''),
        limited,
      };
    })
    .filter((power): power is AllpowersRequiredPower => power !== null)
    .sort((a, b) => b.id - a.id);
}

export async function fetchAllpowersRequiredFromFairtrade(): Promise<AllpowersRequiredPower[]> {
  const res = await fetch(ALLPOWERS_FAIRTRADE_URL, {
    headers: { 'User-Agent': 'xat-ajuda/2.0' },
    cache: 'default',
  });

  if (!res.ok) {
    throw new Error(`FairTrade fetch failed (${res.status})`);
  }

  const html = await res.text();
  const powers = parseAllpowersRequiredFromFairtradeHtml(html);

  if (powers.length === 0) {
    throw new Error('No AllPowers entries parsed from FairTrade');
  }

  return powers;
}

export async function fetchAllpowersRequired(): Promise<AllpowersRequiredResponse> {
  const res = await fetch(XAT_API.allpowersRequired(), { cache: 'no-store' });

  if (!res.ok) {
    throw new Error('Failed to load AllPowers list');
  }

  return res.json() as Promise<AllpowersRequiredResponse>;
}
